import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  Circle,
  Rocket,
  Sparkles,
  X,
  ChevronRight,
} from 'lucide-react';

import { api } from '@/lib/api/client';
import { brandsApi } from '@/features/brands/api';
import { Button } from '@/components/ui/Button';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';

const DISMISSED_KEY = 'xotski.firstEventWizard.dismissed';
const SEEN_ONCE_KEY = 'xotski.firstEventWizard.seenOnce';

/**
 * Caso 14 — Modal-based onboarding wizard. Builds on
 * `FirstStepsChecklist` but pushes the next-step CTA front-and-center on
 * a fresh account.
 *
 * Behaviour:
 *  - First time a new user lands on the dashboard, the modal auto-opens
 *    (unless they've dismissed it before).
 *  - The "Start setup" button on the checklist re-opens it on demand.
 *  - Each step links to the existing creation flow; we never duplicate
 *    forms here.
 *  - Completion is derived by hitting the same list endpoints the
 *    checklist uses — no new backend.
 */
export function FirstEventWizard({
  forceOpen,
  onClose,
}: {
  forceOpen?: boolean;
  onClose?: () => void;
}) {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  // Caso 14 v2 — toggle the inline brand form within the modal. Skips
  // the redirect to /brand-studio/new for the simplest happy path.
  const [brandInline, setBrandInline] = useState(false);
  const [brandName, setBrandName] = useState('');

  const createBrand = useMutation({
    mutationFn: () =>
      brandsApi.create({ name: brandName.trim() }),
    onSuccess: () => {
      toast({ tone: 'success', title: t('firstEventWizard.brand.created') });
      setBrandInline(false);
      setBrandName('');
      // Invalidate the steps queries so the wizard re-renders with the
      // brand step marked done.
      queryClient.invalidateQueries({ queryKey: ['firstSteps', 'brands'] });
    },
    onError: (err: any) =>
      toast({
        tone: 'error',
        title: t('firstEventWizard.brand.createFailed'),
        description: err?.message,
      }),
  });

  // Auto-open once on first dashboard visit, only when no brand exists yet.
  // Subsequent visits stay closed unless the host passes `forceOpen`.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (forceOpen) {
      setOpen(true);
      return;
    }
    const dismissed = window.localStorage.getItem(DISMISSED_KEY) === '1';
    const seenOnce = window.localStorage.getItem(SEEN_ONCE_KEY) === '1';
    if (!dismissed && !seenOnce) {
      setOpen(true);
      window.localStorage.setItem(SEEN_ONCE_KEY, '1');
    }
  }, [forceOpen]);

  const brands = useQuery({
    queryKey: ['firstSteps', 'brands'],
    queryFn: () =>
      api
        .list<{ id: number }>('/co-brands', { page: 1, perPage: 1 })
        .catch(() => ({ items: [], total: 0 })),
    staleTime: 30_000,
    enabled: open,
  });
  const events = useQuery({
    queryKey: ['firstSteps', 'events'],
    queryFn: () =>
      api
        .list<{ id: number }>('/xk-event', { page: 1, perPage: 1 })
        .catch(() => ({ items: [], total: 0 })),
    staleTime: 30_000,
    enabled: open,
  });
  const stations = useQuery({
    queryKey: ['firstSteps', 'stations'],
    queryFn: () =>
      api
        .list<{ id: number }>('/xk-station', { page: 1, perPage: 1 })
        .catch(() => ({ items: [], total: 0 })),
    staleTime: 30_000,
    enabled: open,
  });

  const hasBrand = (brands.data?.total ?? 0) > 0;
  const hasEvent = (events.data?.total ?? 0) > 0;
  const hasStation = (stations.data?.total ?? 0) > 0;
  const firstBrandId = brands.data?.items?.[0]?.id;
  const completedCount = [hasBrand, hasEvent, hasStation].filter(Boolean).length;
  const progress = Math.round((completedCount / 3) * 100);

  function close(remember: boolean) {
    setOpen(false);
    onClose?.();
    if (remember && typeof window !== 'undefined') {
      window.localStorage.setItem(DISMISSED_KEY, '1');
    }
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="first-event-wizard-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div className="w-full max-w-xl rounded-2xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] shadow-2xl">
        <header className="flex items-start justify-between gap-3 border-b border-[var(--color-border-subtle)] p-5">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-[var(--color-accent-soft,rgba(178,98,245,0.12))] p-2 text-[var(--color-accent)]">
              <Rocket className="h-5 w-5" />
            </div>
            <div>
              <h2
                id="first-event-wizard-title"
                className="text-lg font-semibold text-[var(--color-fg-primary)]"
              >
                {t('firstEventWizard.title')}
              </h2>
              <p className="mt-0.5 text-sm text-[var(--color-fg-muted)]">
                {t('firstEventWizard.subtitle')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => close(false)}
            aria-label={t('common.close')}
            className="rounded-md p-1 text-[var(--color-fg-muted)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-fg-primary)]"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="px-5 pt-4">
          <div className="mb-1 flex items-center justify-between text-xs text-[var(--color-fg-muted)]">
            <span>{t('firstEventWizard.progress')}</span>
            <span>{progress}%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--color-bg-elevated)]">
            <div
              className="h-1.5 rounded-full bg-[var(--color-accent)] transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <ol className="space-y-3 p-5">
          <Step
            done={hasBrand}
            number={1}
            title={t('firstEventWizard.step.brand.title')}
            description={t('firstEventWizard.step.brand.description')}
            cta={
              brandInline
                ? t('common.cancel')
                : t('firstEventWizard.step.brand.cta')
            }
            // Caso 14 v2 — open the inline form instead of redirecting.
            // Users who prefer the full Brand Studio experience can still
            // reach it from the sidebar / dashboard.
            onClick={() => setBrandInline((v) => !v)}
          />
          {brandInline && !hasBrand && (
            <li className="rounded-lg border border-dashed border-[var(--color-border-default)] p-3">
              <label className="flex flex-col gap-1 text-xs">
                <span className="text-[var(--color-fg-muted)]">
                  {t('firstEventWizard.brand.nameLabel')}
                </span>
                <input
                  autoFocus
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && brandName.trim()) {
                      createBrand.mutate();
                    }
                  }}
                  placeholder={t('firstEventWizard.brand.namePlaceholder')}
                  className="w-full rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-fg-primary)]"
                />
              </label>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => {
                    close(false);
                    navigate('/brand-studio/new');
                  }}
                  className="text-xs text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]"
                >
                  {t('firstEventWizard.brand.openStudio')}
                </button>
                <Button
                  size="sm"
                  onClick={() => createBrand.mutate()}
                  disabled={!brandName.trim() || createBrand.isPending}
                  loading={createBrand.isPending}
                >
                  {t('firstEventWizard.brand.createSubmit')}
                </Button>
              </div>
            </li>
          )}
          <Step
            done={hasEvent}
            number={2}
            title={t('firstEventWizard.step.event.title')}
            description={t('firstEventWizard.step.event.description')}
            cta={t('firstEventWizard.step.event.cta')}
            disabled={!hasBrand}
            onClick={() => {
              close(false);
              if (firstBrandId) navigate(`/brand/${firstBrandId}`);
              else navigate('/');
            }}
          />
          <Step
            done={hasStation}
            number={3}
            title={t('firstEventWizard.step.station.title')}
            description={t('firstEventWizard.step.station.description')}
            cta={t('firstEventWizard.step.station.cta')}
            disabled={!hasEvent}
            onClick={() => {
              close(false);
              navigate('/stations');
            }}
          />
        </ol>

        <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--color-border-subtle)] p-4">
          <button
            type="button"
            onClick={() => close(true)}
            className="text-xs text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]"
          >
            {t('firstEventWizard.dontShowAgain')}
          </button>
          <Button variant="ghost" size="sm" onClick={() => close(false)}>
            {t('common.close')}
          </Button>
        </footer>
      </div>
    </div>
  );
}

function Step({
  done,
  number,
  title,
  description,
  cta,
  onClick,
  disabled,
}: {
  done: boolean;
  number: number;
  title: string;
  description: string;
  cta: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <li
      className={`flex items-start gap-3 rounded-lg border p-3 ${
        done
          ? 'border-[color-mix(in_srgb,var(--color-success)_30%,transparent)] bg-[color-mix(in_srgb,var(--color-success)_6%,transparent)]'
          : 'border-[var(--color-border-subtle)]'
      }`}
    >
      <div className="mt-0.5 shrink-0">
        {done ? (
          <CheckCircle2 className="h-5 w-5 text-[var(--color-success)]" />
        ) : disabled ? (
          <Circle className="h-5 w-5 text-[var(--color-fg-subtle)]" />
        ) : (
          <div className="grid h-5 w-5 place-items-center rounded-full bg-[var(--color-accent)] text-[10px] font-bold text-[var(--color-fg-on-accent)]">
            {number}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-[var(--color-fg-primary)]">
          {title}
        </div>
        <div className="mt-0.5 text-xs text-[var(--color-fg-muted)]">
          {description}
        </div>
      </div>
      {!done && (
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-[var(--color-border-default)] px-3 py-1 text-xs font-medium hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-40"
        >
          {cta}
          <ChevronRight className="h-3 w-3" />
        </button>
      )}
      {done && (
        <span className="inline-flex shrink-0 items-center gap-1 text-xs text-[var(--color-success)]">
          <Sparkles className="h-3 w-3" /> {/* completion flair */}
        </span>
      )}
    </li>
  );
}
